// Social-Suite tab: review queue (non-blog channels), analytics table,
// honest configured/not-configured status for the P3/P4 motors.
import type { MarketingDeps } from "./marketing.js";
import type { ContentItem } from "./marketing-blog.js";

interface AnalyticsResponse {
  series: Array<{ channel: string; metric: string; points: Array<{ value: number; capturedAt: string }> }>;
}
interface SecretsResponse { secrets: Array<{ key: string; isSet: boolean }> }

export function initSocialTab(m: HTMLElement, deps: MarketingDeps): void {
  const queueHost = m.querySelector("[data-social-queue]") as HTMLElement | null;
  const analyticsHost = m.querySelector("[data-social-analytics]") as HTMLElement | null;
  const statusLine = m.querySelector("[data-social-status]") as HTMLElement | null;
  if (!queueHost || !analyticsHost || !statusLine) return;

  // Zähler für den Publish-Button (wie viele freigegebene Posts gehen gleich raus) — wird nach Setup gesetzt.
  let refreshPubCount: () => void = () => {};

  // ----- Übersicht in der Freigabe-Queue (Uli 12.10.2026) -----
  // „Wenn ich ein Set freigegeben habe, springt er nicht ans Ende, nicht an den Anfang, sondern
  // irgendwo in die Mitte." Ursache: Nach jedem Klick wurde die GANZE Liste neu aufgebaut —
  // erst leer, dann laden die Bilder nach und die Seite wächst wieder; die Scrollposition landete
  // zufällig. Jetzt: (1) nur das erledigte Set verschwindet, die Ansicht springt an den Anfang des
  // nächsten; (2) älteste Sets zuerst, mit „wartet seit …"; (3) eine stehende Leiste zeigt
  // „Set x von n" und springt per Auswahl zu jedem Thema.
  const panel = queueHost.closest(".om-panel") as HTMLElement | null;
  const ueberschrift = queueHost.previousElementSibling?.tagName === "H3" ? queueHost.previousElementSibling as HTMLElement : null;
  const nav = document.createElement("div");
  nav.className = "om-queue-nav";
  nav.hidden = true;
  const navPos = document.createElement("strong");
  const navSel = document.createElement("select");
  navSel.className = "om-queue-nav__sel";
  navSel.title = "Zu einem Thema springen";
  const navMsg = document.createElement("span");
  navMsg.className = "om-queue-nav__msg";
  nav.append(navPos, navSel, navMsg);
  queueHost.before(nav);
  let navMsgTimer = 0;
  const meldeKurz = (text: string): void => {
    navMsg.textContent = text;
    window.clearTimeout(navMsgTimer);
    navMsgTimer = window.setTimeout(() => { navMsg.textContent = ""; }, 8000);
  };
  const karten = (): HTMLElement[] => Array.from(queueHost.querySelectorAll<HTMLElement>(":scope > .om-queue-card"));
  /** Das Set, das gerade oben im sichtbaren Bereich steht (unter der Leiste). */
  const aktuellerIndex = (): number => {
    const ks = karten();
    if (!ks.length) return -1;
    // Aktuell = das letzte Set, dessen Anfang schon unter der Leiste angekommen ist. (Erst
    // „Unterkante unter der Leiste" — nach einem Sprung ragte das vorige Set noch ein paar Pixel
    // hinein und die Anzeige hinkte um eins hinterher; im Test 12.10. aufgefallen.)
    const grenze = (panel ? panel.getBoundingClientRect().top : 0) + nav.offsetHeight + 60;
    let i = 0;
    ks.forEach((k, j) => { if (k.getBoundingClientRect().top <= grenze) i = j; });
    return i;
  };
  const zeigePosition = (): void => {
    const ks = karten();
    if (!ks.length) return;
    const i = aktuellerIndex();
    const beitraege = ks.reduce((s, k) => s + Number(k.dataset.anzahl || 0), 0);
    navPos.textContent = `Set ${i + 1} von ${ks.length} · ${beitraege} Beiträge offen`;
    navSel.value = ks[i]!.dataset.key ?? "";
  };
  const fuelleAuswahl = (): void => {
    navSel.textContent = "";
    karten().forEach((k, i) => {
      const o = document.createElement("option");
      o.value = k.dataset.key ?? "";
      o.textContent = `${i + 1}. ${k.dataset.titel ?? ""} · ${k.dataset.alter ?? ""}`;
      navSel.appendChild(o);
    });
  };
  const leereQueue = (): void => {
    const p = document.createElement("p");
    p.className = "dim";
    p.textContent = "Nichts zur Freigabe. Neue Beiträge erscheinen hier, sobald ein Motor gelaufen ist — sie gehen erst nach dem Haken raus.";
    queueHost.appendChild(p);
  };
  const aktualisiereNav = (): void => {
    const ks = karten();
    // Anzahl in die Ueberschrift: sonst sieht man nicht, ob nichts wartet oder ob nur
    // nichts geladen hat.
    if (ueberschrift) {
      ueberschrift.textContent = ks.length ? `Freigabe-Queue — ${ks.length} ${ks.length === 1 ? "Thema wartet" : "Themen warten"}` : "Freigabe-Queue — nichts offen";
    }
    nav.hidden = !ks.length;
    zeigePosition();
  };
  navSel.addEventListener("change", () => {
    karten().find((k) => k.dataset.key === navSel.value)?.scrollIntoView({ block: "start" });
  });
  let positionGeplant = false;
  (panel ?? window).addEventListener("scroll", () => {
    if (positionGeplant) return;
    positionGeplant = true;
    requestAnimationFrame(() => { positionGeplant = false; zeigePosition(); });
  }, { passive: true });
  /** created_at kommt als ISO („…Z") oder als SQLite-Zeit („2026-09-28 10:47:56", UTC). */
  const zeitpunkt = (s: string): number => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s.replace(" ", "T")}Z`).getTime();

  const loadQueue = async (): Promise<void> => {
    // Neuaufbau (nach Neu-Generieren, Metricool-Versand, neuem Motorlauf) soll die Ansicht nicht
    // verschieben: Bezugspunkt merken — das oben stehende Set, oder den Kasten unter der Queue,
    // wenn man gerade dort arbeitet — und danach genau dorthin zurück.
    const vorher = karten();
    const panelOben = panel ? panel.getBoundingClientRect().top : 0;
    const inQueue = vorher.some((k) => { const b = k.getBoundingClientRect(); return b.bottom > panelOben && b.top < panelOben + (panel?.clientHeight ?? window.innerHeight); });
    const merkKey = inQueue && (panel?.scrollTop ?? 0) > 5 ? vorher[aktuellerIndex()]?.dataset.key : undefined;
    const nachQueue = queueHost.nextElementSibling as HTMLElement | null;
    const nachQueueTop = !inQueue && nachQueue && (panel?.scrollTop ?? 0) > 5 ? nachQueue.getBoundingClientRect().top : null;

    const r = await deps.api<{ items: ContentItem[] }>("/api/marketing/content?status=review");
    // Älteste zuerst — die Schnittstelle liefert die neuesten oben, dadurch rutschten alte Sets
    // immer ans Ende und blieben liegen.
    const items = (r.data?.items ?? []).filter((i) => i.channel !== "blog")
      .sort((a, b) => zeitpunkt(a.created_at) - zeitpunkt(b.created_at) || a.id - b.id);
    refreshPubCount();
    queueHost.textContent = "";
    if (!items.length) {
      leereQueue();
      fuelleAuswahl();
      aktualisiereNav();
      return;
    }
    // Nach Thema (target_url) gruppieren — die 3 Kanal-Posts eines Themas gehören zusammen.
    const groups = new Map<string, ContentItem[]>();
    for (const it of items) { const k = it.target_url ?? `#${it.id}`; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(it); }
    for (const [key, group] of groups) {
      const first = group[0]!;
      const hasVideo = group.some((it) => it.type.startsWith("social_video"));
      const card = document.createElement("details");
      card.className = "om-card om-queue-card";
      // Aufgeklappt, nicht zugeklappt (Uli 20.08.2026). Ein zugeklappter Kasten laesst sich
      // freigeben, ohne das Bild je gesehen zu haben — so ging der falsch angesetzte Fuss durch.
      card.open = true;
      const tage = Math.max(0, Math.floor((Date.now() - zeitpunkt(first.created_at)) / 864e5));
      const alter = tage === 0 ? "seit heute" : tage === 1 ? "seit 1 Tag" : `seit ${tage} Tagen`;
      card.dataset.key = key;
      card.dataset.titel = first.title ?? "(ohne Titel)";
      card.dataset.alter = alter;
      card.dataset.anzahl = String(group.length);
      const sum = document.createElement("summary");
      sum.textContent = `${hasVideo ? "🎬 " : ""}${first.title ?? "(ohne Titel)"} · ${group.length} Kanäle · `;
      const alterTag = document.createElement("span");
      alterTag.className = tage >= 7 ? "om-queue-alter om-queue-alter--alt" : "om-queue-alter";
      alterTag.textContent = `wartet ${alter}`;
      sum.appendChild(alterTag);
      card.appendChild(sum);

      for (const item of group) {
        const block = document.createElement("div");
        block.style.cssText = "margin:10px 0;padding-top:10px;border-top:1px solid #eee";
        const lbl = document.createElement("div");
        lbl.style.cssText = "font-weight:600;font-size:12px;text-transform:uppercase;color:#14746a";
        lbl.textContent = (item.type.startsWith("social_video") ? "🎬 " : "")
          + (item.channel === "gmb" ? "Google Business" : item.channel);
        block.appendChild(lbl);

        const ref = item.external_ref ?? "";
        if (item.type.startsWith("social_video") && /^https?:\/\//.test(ref)) {
          const v = document.createElement("video");
          // Gleicher Weg wie in der Videoliste: ueber die eigene Anwendung, damit
          // Inhaltsblocker und ablaufende S3-Adressen die Vorschau nicht killen.
          v.src = `/api/marketing/content/${item.id}/media`;
          v.controls = true; v.playsInline = true; v.preload = "metadata";
          v.style.cssText = "display:block;width:100%;max-width:320px;border-radius:10px;margin:8px 0;background:#000";
          block.appendChild(v);
        } else if (!ref || !ref.includes("/")) {
          // Alt-Eintraege aus Testlaeufen haben in external_ref keinen Pfad, sondern z. B.
          // „stamp=2026-08-11T05-03-24". Frueher endete das in einem gebrochenen Bild.
          const hin = document.createElement("div");
          hin.style.cssText = "color:#ffb020;font-size:13px;margin:6px 0";
          hin.textContent = "Kein Medium hinterlegt — Alt-Eintrag aus einem Testlauf, nicht sendefähig.";
          block.appendChild(hin);
        } else {
          // Gross genug, um Finger und Zehen zu zaehlen. 240 px waren es vorher — darauf
          // sieht man einen falsch angesetzten Fuss schlicht nicht.
          const img = document.createElement("img");
          img.loading = "lazy"; img.alt = item.title ?? "";
          img.src = `/api/marketing/content/${item.id}/image`;
          img.title = "Klicken für Originalgröße";
          img.style.cssText = "display:block;width:100%;max-width:620px;border-radius:10px;margin:8px 0;box-shadow:0 4px 14px rgba(0,0,0,.18);cursor:zoom-in";
          img.addEventListener("error", () => img.remove());
          img.addEventListener("click", () => zeigeGross(img.src, item.title ?? ""));
          block.appendChild(img);
        }

        // Befunde der Maschine — Hinweise, keine Entscheidung. Zwei getrennte Fragen:
        // „stimmt die Anatomie?" und „gehört das Motiv zum Thema?". Ein Fuß in einer
        // Halsmassage ist anatomisch tadellos — deshalb zwei Zeilen statt einer.
        const medium = item.type.startsWith("social_video") ? "Video" : "Bild";
        const zeile = (text: string, farbe: string, fett = false): void => {
          const d = document.createElement("div");
          d.style.cssText = `font-size:12px;margin:3px 0;color:${farbe}` + (fett ? ";font-weight:600" : "");
          d.textContent = text;
          block.appendChild(d);
        };
        const befund = (
          name: string,
          b: { ok: number; gelaufen: number; probleme: string | null } | null | undefined,
          nieText: string,
          okText: string,
        ): void => {
          if (!b) zeile(`⚠ ${name}: kein Befund gespeichert — ${nieText}`, "#ffb020");
          else if (!b.gelaufen) zeile(`⚠ ${name} fehlgeschlagen: ${b.probleme ?? "Grund unbekannt"}`, "#ffb020");
          else if (b.ok) zeile(`${name}: ${okText}`, "var(--muted)");
          else zeile(`✗ ${name} beanstandet: ${b.probleme ?? ""}`, "#ff6b52", true);
        };
        befund("Anatomie", item.pruefung, `dieses ${medium} wurde nie geprüft`,
          "ohne Befund (findet erfahrungsgemäß nicht jeden Fehler)");
        // Presenter-Videos bekommen KEINE Themenpruefung, und die Warnung dafuer war
        // irrefuehrend (Uli 03.09.2026): sie las sich wie eine ausgefallene Pruefung,
        // dabei gibt es hier nichts zu pruefen — der Avatar steht vor einem von fuenf
        // festen Hintergruenden, den eine Wortliste zuordnet. Also die Zuordnung zeigen.
        const hg = item.pruefung_hintergrund?.probleme;
        if (hg) {
          zeile(`Thema: fester Hintergrund „${hg}" — per Wortliste zugeordnet, kein Modellurteil`,
            "var(--muted)");
        } else {
          // Der Hinweis auf den 21.08. gilt nur fuer Bilder; ein Video von Ende August
          // war schlicht nie an der Reihe. Beim Datum zu bleiben hiesse, den Freigebenden
          // in die falsche Richtung zu schicken.
          befund("Thema", item.pruefung_thema,
            medium === "Video" ? "dieses Video wurde nicht auf Themenbindung geprüft"
              : "erst ab 21.08.2026 geprüft",
            "passt zum Beitrag");
        }

        const body = document.createElement("pre");
        body.className = "om-body"; body.textContent = item.body_text ?? "";
        block.appendChild(body);
        card.appendChild(block);
      }

      const actions = document.createElement("div");
      actions.className = "modal-actions inline";
      const msg = document.createElement("span"); msg.className = "dim";

      // Der Haken (Uli 20.08.2026): freigeben erst, wenn ausdruecklich bestaetigt ist, dass
      // die Motive angesehen wurden. Bewusst pro Thema, nicht global — sonst hakt man einmal
      // ab und winkt danach alles durch.
      const hakenWrap = document.createElement("label");
      hakenWrap.style.cssText = "display:flex;flex-direction:row;align-items:center;justify-content:flex-start;gap:8px;margin:10px 0;font-size:13px;cursor:pointer;color:var(--fg)";
      const haken = document.createElement("input");
      haken.type = "checkbox";
      haken.style.cssText = "width:17px;height:17px;cursor:pointer";
      hakenWrap.append(haken, document.createTextNode("Angesehen — Hände, Füße, Gliedmaßen und Text geprüft"));
      card.appendChild(hakenWrap);

      const approveAll = document.createElement("button");
      approveAll.className = "btn-primary"; approveAll.textContent = `✓ Alle ${group.length} freigeben`;
      approveAll.disabled = true;
      approveAll.title = "Erst den Haken setzen";
      haken.addEventListener("change", () => {
        approveAll.disabled = !haken.checked;
        approveAll.title = haken.checked ? "" : "Erst den Haken setzen";
      });
      const rejectAll = document.createElement("button");
      rejectAll.className = "btn-secondary"; rejectAll.textContent = "✗ Set ablehnen";

      // Antworten AUSWERTEN statt verwerfen (Uli 25.08.2026: „ich klicke und es passiert
      // nichts"). Vorher stand hier `void Promise.all(...).then(loadQueue)` — ein 409
      // (die Karte war laengst freigegeben, die Ansicht nur aelter), eine abgelaufene
      // Sitzung und ein echter Serverfehler sahen alle gleich aus: wie ein toter Knopf.
      const setzeStatus = async (btn: HTMLButtonElement, status: string): Promise<void> => {
        btn.disabled = true;
        msg.textContent = "";
        const antworten = await Promise.all(group.map((it) =>
          deps.api<{ error?: string }>(`/api/marketing/content/${it.id}/status`, "POST", { status })));
        const fehler = antworten.filter((a) => a.status !== 200);
        const f = fehler[0];
        if (f) {
          const grund = f.data?.error;
          msg.textContent =
            grund === "invalid_transition" ? "Bereits erledigt — diese Ansicht war älter. Liste wird neu geladen."
            : f.status === 401 ? "Sitzung abgelaufen — bitte neu anmelden."
            : `Fehler bei ${fehler.length} von ${antworten.length}: ${grund ?? f.status}`;
          btn.disabled = false;
          // Nur eine veraltete Ansicht neu laden — bei anderen Fehlern bleibt die Karte samt
          // Meldung stehen (ein Neuaufbau hätte die Meldung sofort wieder gelöscht).
          if (grund === "invalid_transition") { meldeKurz(msg.textContent); await loadQueue(); }
          return;
        }
        // Erfolg: nur dieses Set herausnehmen und an den Anfang des nächsten springen.
        meldeKurz(`${status === "approved" ? "✓ freigegeben" : "✗ abgelehnt"}: ${(first.title ?? "Set").trim()} (${group.length})`);
        refreshPubCount();
        const naechste = (card.nextElementSibling ?? card.previousElementSibling) as HTMLElement | null;
        card.remove();
        fuelleAuswahl();
        if (!karten().length) leereQueue();
        else if (naechste?.classList.contains("om-queue-card")) naechste.scrollIntoView({ block: "start" });
        aktualisiereNav();
      };

      approveAll.addEventListener("click", () => { void setzeStatus(approveAll, "approved"); });
      rejectAll.addEventListener("click", () => { void setzeStatus(rejectAll, "rejected"); });
      const regen = document.createElement("button");
      regen.className = "btn-secondary"; regen.textContent = "↻ Set neu generieren";
      regen.addEventListener("click", () => {
        regen.disabled = true;
        msg.textContent = hasVideo ? "Neues Video wird erzeugt … (einige Minuten)" : "Generiere das Set neu … (1–2 Min)";
        const [pfad, nutzlast, jobArt] = hasVideo
          ? ["/api/marketing/video/regenerate", { targetUrl: first.target_url }, /\/hg-audio\/pv_/.test(first.target_url ?? "") ? "presenter_video" : "video_motor"]
          : ["/api/marketing/social/regenerate", { contentId: first.id }, "social_gen"];
        void deps.api<{ error?: string }>(pfad as string, "POST", nutzlast).then((rr) => {
          if (rr.status !== 200) { msg.textContent = `Fehler: ${rr.data?.error ?? rr.status}`; regen.disabled = false; return; }
          const poll = (): void => {
            void deps.api<{ jobs: Array<{ status: string }> }>(`/api/marketing/jobs?type=${jobArt}`).then((j) => {
              const l = j.data?.jobs?.[0];
              if (l && l.status !== "running") { msg.textContent = "Fertig ✓"; void loadQueue(); } else { setTimeout(poll, 5000); }
            });
          };
          setTimeout(poll, 5000);
        });
      });
      actions.append(approveAll, rejectAll, regen, msg);
      card.appendChild(actions);
      queueHost.appendChild(card);
    }
    fuelleAuswahl();
    if (merkKey) karten().find((k) => k.dataset.key === merkKey)?.scrollIntoView({ block: "start" });
    else if (nachQueue && nachQueueTop !== null && panel) panel.scrollTop += nachQueue.getBoundingClientRect().top - nachQueueTop;
    aktualisiereNav();
  };

  /** Bild bildschirmfuellend zeigen — Zehen zaehlt man nicht auf einer Briefmarke. */
  function zeigeGross(src: string, alt: string): void {
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.88);"
      + "display:flex;align-items:center;justify-content:center;cursor:zoom-out;padding:16px";
    const big = document.createElement("img");
    big.src = src; big.alt = alt;
    big.style.cssText = "max-width:100%;max-height:100%;object-fit:contain;border-radius:6px";
    overlay.appendChild(big);
    const zu = (): void => { overlay.remove(); document.removeEventListener("keydown", esc); };
    const esc = (e: KeyboardEvent): void => { if (e.key === "Escape") zu(); };
    overlay.addEventListener("click", zu);
    document.addEventListener("keydown", esc);
    document.body.appendChild(overlay);
  }

  const loadAnalytics = async (): Promise<void> => {
    const r = await deps.api<AnalyticsResponse>("/api/marketing/analytics?profile=bildung");
    const series = r.data?.series ?? [];
    analyticsHost.textContent = "";
    if (!series.length) {
      const p = document.createElement("p");
      p.className = "dim";
      p.textContent = "Noch keine Analytics-Daten. Befüllt sich, sobald der Analytics-Pull läuft (braucht METRICOOL_API_TOKEN, P4).";
      analyticsHost.appendChild(p);
      return;
    }
    const table = document.createElement("table");
    table.className = "om-table";
    const head = table.createTHead().insertRow();
    for (const label of ["Kanal", "Metrik", "Aktuell", "Δ seit Beginn", "Stand"]) {
      const th = document.createElement("th");
      th.textContent = label;
      head.appendChild(th);
    }
    const tbody = table.createTBody();
    for (const s of series) {
      const first = s.points[0]!;
      const last = s.points[s.points.length - 1]!;
      const tr = tbody.insertRow();
      tr.insertCell().textContent = s.channel;
      tr.insertCell().textContent = s.metric;
      tr.insertCell().textContent = String(last.value);
      const delta = last.value - first.value;
      tr.insertCell().textContent = `${delta >= 0 ? "+" : ""}${delta}`;
      tr.insertCell().textContent = deps.formatDate(last.capturedAt);
    }
    analyticsHost.appendChild(table);
  };

  const loadStatus = async (): Promise<void> => {
    const r = await deps.api<SecretsResponse>("/api/marketing/secrets");
    const isSet = (k: string): boolean => r.data?.secrets.find((s) => s.key === k)?.isSet ?? false;
    const parts = [
      `Content-Generierung (P3): ${isSet("ANTHROPIC_API_KEY") ? "Text-Key ✓" : "ANTHROPIC_API_KEY fehlt"}, ${isSet("OPENAI_API_KEY") ? "Bild-Key ✓" : "OPENAI_API_KEY fehlt"}`,
      `Publishing/Analytics via Metricool (P4): ${isSet("METRICOOL_API_TOKEN") ? "Token ✓" : "METRICOOL_API_TOKEN fehlt — nicht konfiguriert"}`,
    ];
    statusLine.textContent = parts.join(" · ");
  };

  // ----- Generier-Formular: aus einem Ratgeber ein 3-Kanal-Post-Set erzeugen -----
  const gen = document.createElement("div");
  gen.className = "om-card";
  const gTitle = document.createElement("strong");
  gTitle.textContent = "Neue Posts erzeugen";
  gen.appendChild(gTitle);
  const row = document.createElement("div");
  row.className = "modal-actions inline";
  const portalSel = document.createElement("select");
  for (const [v, l] of [["wellnessinperfektion", "WIP"], ["swav-berlin", "SWAV"], ["wellnessheimstudium", "WHI"]] as [string, string][]) {
    const o = document.createElement("option"); o.value = v; o.textContent = l; portalSel.appendChild(o);
  }
  // Ratgeber-Dropdown (nach Titel/Hits) statt roher ID — lädt je Portal, ohne bereits bepostete.
  const articleSel = document.createElement("select");
  articleSel.style.minWidth = "260px";
  const setArticlePlaceholder = (text: string): void => {
    articleSel.textContent = "";
    const o = document.createElement("option"); o.value = ""; o.textContent = text; o.disabled = true; o.selected = true;
    articleSel.appendChild(o);
  };
  const loadArticles = async (): Promise<void> => {
    setArticlePlaceholder("Ratgeber lädt …");
    const r = await deps.api<{ articles?: Array<{ id: number; title: string }>; error?: string }>(
      `/api/marketing/social/articles?portal=${encodeURIComponent(portalSel.value)}`,
    );
    if (r.status !== 200 || !r.data?.articles) { setArticlePlaceholder(r.data?.error === "live_db_unreachable" ? "(Liste nicht ladbar)" : "(Fehler beim Laden)"); return; }
    const list = r.data.articles;
    if (!list.length) { setArticlePlaceholder("(alle Ratgeber haben schon einen Post)"); return; }
    articleSel.textContent = "";
    const ph = document.createElement("option"); ph.value = ""; ph.textContent = `– Ratgeber wählen (${list.length}) –`; ph.disabled = true; ph.selected = true;
    articleSel.appendChild(ph);
    for (const a of list) { const o = document.createElement("option"); o.value = String(a.id); o.textContent = a.title; articleSel.appendChild(o); }
  };
  setArticlePlaceholder("Ratgeber lädt …");
  portalSel.addEventListener("change", () => { void loadArticles(); });
  const typeSel = document.createElement("select");
  for (const [v, l] of [["image", "📷 Bild-Post"], ["video", "🎬 Video-Reel"]] as [string, string][]) {
    const o = document.createElement("option"); o.value = v; o.textContent = l; typeSel.appendChild(o);
  }
  const styleSel = document.createElement("select");
  for (const [v, l] of [["", "Stil: automatisch"], ["problem", "Problem-Moment"], ["hook", "Text-Hook"]] as [string, string][]) {
    const o = document.createElement("option"); o.value = v; o.textContent = l; styleSel.appendChild(o);
  }
  // Stil-Auswahl nur bei Bild-Posts relevant (Video nutzt keinen Stil).
  typeSel.addEventListener("change", () => { styleSel.style.display = typeSel.value === "video" ? "none" : ""; });
  const expertLbl = document.createElement("label");
  const expertCb = document.createElement("input"); expertCb.type = "checkbox";
  expertLbl.appendChild(expertCb); expertLbl.appendChild(document.createTextNode(" Experten-Tipp"));
  const genBtn = document.createElement("button");
  genBtn.className = "btn-primary"; genBtn.textContent = "Posts erzeugen";
  const genMsg = document.createElement("span"); genMsg.className = "dim";
  row.append(portalSel, articleSel, typeSel, styleSel, expertLbl, genBtn, genMsg);
  gen.appendChild(row);

  // Freigegebene Posts als geplante Entwürfe an Metricool senden (P4, kein Live-Post)
  const pubRow = document.createElement("div");
  pubRow.className = "modal-actions inline";
  const pubBtn = document.createElement("button");
  pubBtn.className = "btn-primary";
  pubBtn.style.cssText = "background:#1f9d55;border-color:#1f9d55;color:#fff";
  pubBtn.textContent = "Freigegebene → Metricool (Entwürfe)";
  const pubMsg = document.createElement("span"); pubMsg.className = "dim";
  pubRow.append(pubBtn, pubMsg);
  gen.appendChild(pubRow);
  // Button-Beschriftung mit Anzahl freigegebener Posts (= Entwürfe, die der Klick sendet)
  refreshPubCount = (): void => {
    void deps.api<{ items: ContentItem[] }>("/api/marketing/content?status=approved").then((r) => {
      const n = (r.data?.items ?? []).filter((i) => i.channel !== "blog").length;
      pubBtn.textContent = `Freigegebene → Metricool (${n})`;
      pubBtn.disabled = n === 0;
    });
  };
  refreshPubCount();
  pubBtn.addEventListener("click", () => {
    pubBtn.disabled = true;
    pubMsg.textContent = "Sende freigegebene Posts als Entwürfe an Metricool …";
    void deps.api<{ ok?: boolean; error?: string }>("/api/marketing/motors/social_publish/run", "POST").then((r) => {
      if (r.status !== 200) { pubMsg.textContent = `Fehler: ${r.data?.error ?? r.status}`; pubBtn.disabled = false; return; }
      const poll = (): void => {
        void deps.api<{ jobs: Array<{ status: string; log?: string }> }>("/api/marketing/jobs?type=social_publish").then((j) => {
          const latest = j.data?.jobs?.[0];
          if (latest && latest.status !== "running") {
            pubMsg.textContent = (latest.status === "done" ? `Fertig ✓ — ${latest.log ?? ""}` : `Fehler: ${latest.log ?? ""}`).slice(0, 140);
            pubBtn.disabled = false; void loadQueue();
          } else { setTimeout(poll, 4000); }
        });
      };
      setTimeout(poll, 4000);
    });
  });

  // Uli 21.08.2026: „Wo kann ich Posts freigeben? Ich sehe keine Buttons?" — sie standen
  // unter der Überschrift „Freigabe-Queue", aber HINTER dem Erzeugen-Kasten und der
  // Videoliste, also weit unten ausserhalb des Sichtfelds. Was die Überschrift verspricht,
  // muss direkt darunter stehen; Werkzeuge kommen danach.
  queueHost.after(gen);

  // ----- Geplante Videos: In-Suite-Vorschau (Reels gehen per Auto-Publish zu Metricool) -----
  const vidWrap = document.createElement("div");
  vidWrap.className = "om-card";
  const vidTitle = document.createElement("strong"); vidTitle.textContent = "🎬 Geplante Videos";
  vidWrap.appendChild(vidTitle);
  const vidHost = document.createElement("div");
  vidWrap.appendChild(vidHost);
  const loadVideos = async (): Promise<void> => {
    const r = await deps.api<{ videos?: Array<{ id: number; title: string | null; video: string | null; channels: string | null; scheduled_for: string | null; target_url: string | null }> }>("/api/marketing/social/videos");
    vidHost.textContent = "";
    const list = r.data?.videos ?? [];
    if (!list.length) {
      const p = document.createElement("p"); p.className = "dim";
      p.textContent = "Keine geplanten Videos. Reels erscheinen hier, sobald der Video-Motor läuft (Mi-Cron oder Video-Reel oben).";
      vidHost.appendChild(p); return;
    }
    for (const v of list) {
      const block = document.createElement("div");
      block.style.cssText = "margin:10px 0;padding-top:8px;border-top:1px solid #eee";
      const h = document.createElement("div");
      h.style.cssText = "font-weight:600;font-size:13px;margin-bottom:6px";
      h.textContent = `${v.title ?? "(ohne Titel)"}${v.scheduled_for ? ` · geplant ${v.scheduled_for}` : ""}${v.channels ? ` · ${v.channels}` : ""}`;
      block.appendChild(h);
      if (v.video) {
        const vid = document.createElement("video");
        vid.controls = true; vid.preload = "metadata";
        // Ueber die eigene Anwendung, nicht direkt vom fremden Host: Inhaltsblocker
        // schneiden `metricool-temp.s3.amazonaws.com` weg, und die dortigen Adressen
        // laufen ab. Siehe /api/marketing/content/:id/media.
        vid.src = `/api/marketing/content/${v.id}/media`;
        vid.style.cssText = "display:block;width:100%;max-width:300px;border-radius:8px;box-shadow:0 4px 14px rgba(0,0,0,.18)";
        vid.addEventListener("error", () => {
          const box = document.createElement("div");
          box.style.cssText = "font-size:13px;margin:6px 0";
          const hinweis = document.createElement("div");
          hinweis.style.cssText = "color:#ffb020;margin-bottom:4px";
          hinweis.textContent = "⚠ Vorschau lässt sich nicht abspielen — die Videodatei ist nicht erreichbar.";
          const a = document.createElement("a");
          a.href = v.video!; a.target = "_blank"; a.rel = "noopener";
          a.textContent = "Video in neuem Tab öffnen";
          box.append(hinweis, a);
          vid.replaceWith(box);
        });
        block.appendChild(vid);
      }
      if (v.target_url) {
        const actions = document.createElement("div");
        actions.className = "modal-actions inline"; actions.style.cssText = "margin-top:6px";
        const rmsg = document.createElement("span"); rmsg.className = "dim";
        const vregen = document.createElement("button");
        vregen.className = "btn-secondary"; vregen.textContent = "↻ Video neu generieren";
        vregen.addEventListener("click", () => {
          if (!confirm("Dieses Video verwerfen (auch in Metricool) und ein neues generieren? Dauert einige Minuten.")) return;
          vregen.disabled = true; rmsg.textContent = "Altes Video wird verworfen, neues generiert … (einige Min)";
          void deps.api<{ error?: string }>("/api/marketing/video/regenerate", "POST", { targetUrl: v.target_url }).then((rr) => {
            if (rr.status !== 200) { rmsg.textContent = `Fehler: ${rr.data?.error ?? rr.status}`; vregen.disabled = false; return; }
            const poll = (): void => {
              void deps.api<{ jobs: Array<{ status: string }> }>("/api/marketing/jobs?type=video_motor").then((j) => {
                const l = j.data?.jobs?.[0];
                if (l && l.status !== "running") { rmsg.textContent = "Neues Video fertig ✓"; void loadVideos(); } else { setTimeout(poll, 5000); }
              });
            };
            setTimeout(poll, 5000);
          });
        });
        actions.append(vregen, rmsg);
        block.appendChild(actions);
      }
      vidHost.appendChild(block);
    }
  };
  gen.after(vidWrap);

  genBtn.addEventListener("click", () => {
    const articleId = Number(articleSel.value);
    if (!Number.isInteger(articleId) || articleId <= 0) { genMsg.textContent = "Bitte einen Ratgeber aus der Liste wählen."; return; }
    const isVideo = typeSel.value === "video";
    const body: Record<string, unknown> = { portal: portalSel.value, articleId };
    if (!isVideo && styleSel.value) body.style = styleSel.value;
    if (expertCb.checked) body.expert = true;
    const endpoint = isVideo ? "/api/marketing/social/generate-video" : "/api/marketing/social/generate";
    const jobType = isVideo ? "video_motor" : "social_gen";
    genBtn.disabled = true;
    genMsg.textContent = isVideo ? "Video-Motor läuft … (mehrere Min: Bild + Reel + Upload)" : "Motor läuft … (1–2 Min: Texte + 3 Bilder)";
    void deps.api<{ ok?: boolean; error?: string }>(endpoint, "POST", body).then((r) => {
      if (r.status !== 200) { genMsg.textContent = `Fehler: ${r.data?.error ?? r.status}`; genBtn.disabled = false; return; }
      const poll = (): void => {
        void deps.api<{ jobs: Array<{ status: string; log?: string }> }>(`/api/marketing/jobs?type=${jobType}`).then((j) => {
          const latest = j.data?.jobs?.[0];
          if (latest && latest.status !== "running") {
            genMsg.textContent = latest.status === "done"
              ? (isVideo ? "Video fertig ✓ — in Metricool (geplant Freitag)." : "Fertig ✓ — Posts in der Queue.")
              : `Motor-Fehler: ${latest.log ?? ""}`;
            genBtn.disabled = false;
            void loadQueue();
          } else {
            setTimeout(poll, 5000);
          }
        });
      };
      setTimeout(poll, 5000);
    });
  });

  // Follower-Zahlen aus Metricool nachziehen (→ om_analytics → Chart)
  const anaWrap = document.createElement("div");
  anaWrap.className = "modal-actions inline";
  const anaBtn = document.createElement("button");
  anaBtn.className = "btn-secondary";
  anaBtn.textContent = "Follower-Zahlen aktualisieren";
  const anaMsg = document.createElement("span"); anaMsg.className = "dim";
  anaWrap.append(anaBtn, anaMsg);
  analyticsHost.parentElement?.insertBefore(anaWrap, analyticsHost);
  anaBtn.addEventListener("click", () => {
    anaBtn.disabled = true;
    anaMsg.textContent = "Hole Follower-Zahlen aus Metricool …";
    void deps.api<{ error?: string }>("/api/marketing/motors/social_analytics/run", "POST").then((r) => {
      if (r.status !== 200) { anaMsg.textContent = `Fehler: ${r.data?.error ?? r.status}`; anaBtn.disabled = false; return; }
      const poll = (): void => {
        void deps.api<{ jobs: Array<{ status: string }> }>("/api/marketing/jobs?type=social_analytics").then((j) => {
          const latest = j.data?.jobs?.[0];
          if (latest && latest.status !== "running") { anaMsg.textContent = "Aktualisiert ✓"; anaBtn.disabled = false; void loadAnalytics(); }
          else { setTimeout(poll, 4000); }
        });
      };
      setTimeout(poll, 4000);
    });
  });

  void loadQueue();
  void loadAnalytics();
  void loadStatus();
  void loadArticles();
  void loadVideos();
}
